import { useAppConfig } from "~/app/config/useAppConfig";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { type MouseEvent } from "react";
import { MAX_QUANTITY } from "~/utils/constants";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { userIsCustomer, userIsPublic } from "~/domains/user/utils/roles";

import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectIsCartAmountsIsLoading } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";

import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { trackPdpClick, trackPdpWishlistClick } from "~/app/analytics/ga/ga";
import { mapOptionalParts4Wishlist } from "~/features/wishlist/components/modals/AddToWishlist/utils";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { usePackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { isKITProduct, type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { canSeeAddToCartButton } from "~/utils/features";
import { HousingMessages } from "~/domains/housing/components/HousingMessages";
import type { AddToCartFormDraft, OptionalPartDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { useFormContext } from "react-hook-form";
import { openAddToWishlistWizard } from "~/features/wishlist/components/modals/AddToWishlist/AddToWishlistService";
import {
  shouldShowPriceReference,
  usePdpSummaryLogic,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { getIsContractAvailable } from "~/features/productDetails/redux/productDetailsSelectors";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { getRedirectToRegisterPage, redirectToUrl } from "~/utils/url/urlUtils";
import { Alert } from "~/common/mui/Alert/Alert";
import { InfoOrderHandlingUnitQuantity } from "~/features/productDetails/components/ProductSummary/InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Box, Stack } from "~/common/mui";
import {
  getPurchaseDataFromUrl,
  selectAmountInCart,
} from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { renderButtons } from "~/features/productDetails/components/ProductSummary/components/Buttons/usePdpButtons";
import { TabId } from "~/features/productDetails/components/usePDPTabs";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import { useDisplayMMDAssistant } from "~/features/productDetails/context/DisplayMMDAssistantContext";
import { usePdpCmnLogic } from "~/features/productDetails/components/MMDAssistant/logic/usePdpCmnLogic";
import { Product } from "~/domains/product/Product";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { MobileVariantDrawer } from "./components/MobileVariantDrawer";
import { useVariantContext } from "../../context/VariantContext";
import { VariantSelect } from "./components/VariantSelect";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { ProductDetailsSection } from "./components/ProductDetailsSection";
import { PdpPriceBlock } from "./components/PdpPriceBlock";

interface Props {
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  hasPreferredProducts: boolean;
  changeTab: (tabId: string) => void;
}

/*
 * Product Details page summary (starting from breadcrumbs, finishing before tabs) for all other cases.
 * Non-salesman, non-distributor - meaning it can be OEM purchaser, registered, anonymous (and others)
 */
export const MobilePdpSummary = ({ productDescriptor, changeTab }: Props) => {
  const { t } = useTranslation();
  const reco_place = usePurchasePlace();

  const displayMMDAssistant = useDisplayMMDAssistant();

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;
  const formProps = useFormContext<AddToCartFormDraft>();
  const { main } = useAddToCartDerived();
  const quantity = formProps.watch("quantity");
  const packCode = formProps.watch("selectedPackCode");
  const priceReference = formProps.watch("priceReference");
  const pack: PackagingInformationEntry | null = main.selectedPack;
  const packSize = main.selectedPackaging?.packSize ?? pack?.packSize ?? 1;

  const { variantLoading } = useVariantContext();

  const optionalParts = formProps.watch("optionalParts") as Record<string, OptionalPartDraft>;

  const qtyInCart = useSelector(
    selectAmountInCart({
      product: productDescriptor,
      packCode,
    }),
  );
  const isCartLoading = useSelector(selectIsCartAmountsIsLoading);

  const {
    buttons: { showWishlistButton },
    flags: { showBetterDeal },
  } = usePdpSummaryLogic({
    product: productDescriptor,
    qtyInCart,
    quantity,
    packCode: "",
  });

  const showCartButton = canSeeAddToCartButton(true);

  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const { isLoading: isPriceLoading, availability: availabilityInfo } = productDescriptor;
  const isLoading = isPriceLoading || isCartLoading;
  const isError = productDescriptor.isError && !webservicesDisabled;
  const product = productDescriptor.base;
  const isOptime = product.isOptime;
  const isPMA = isProductPMA(productDescriptor);
  const isKit = isKITProduct(product);
  const quantityVisible = product.quantityVisible && !(product.externalProduct && isProductPMA(productDescriptor));
  const isExternalProduct = product.externalProduct;
  const { selection } = usePriceReferenceSelection();
  const isPriceRefVisible = shouldShowPriceReference(isExternalProduct, productDescriptor, selection);

  const tariffSurcharge = isTariffSurchargeEnabled() && (main.netPrice?.tariffSurcharge ?? false);

  // TODO: What the hell can I do with this...
  const priceInfo = productDescriptor.prices[0];
  const isPriceAt15Digit = productDescriptor.isMultiPrice;

  const isContractItem = getIsContractAvailable(productDescriptor);

  let isAddToCartEnabled;
  if (isContractItem) {
    isAddToCartEnabled = userHasPermission(Permission.VIEW_CONTRACT) && userHasPermission(Permission.ADD_TO_CART);
  } else if (!isPriceAt15Digit) {
    isAddToCartEnabled = priceInfo && priceInfo.hasPrice && availabilityInfo && availabilityInfo.hasPackaging;
  } else {
    isAddToCartEnabled = availabilityInfo && availabilityInfo.hasPackaging;
  }

  const qtyInputEnabled = !isLoading && ((showCartButton && !isError) || showWishlistButton) && product.quantityVisible;

  let addToCartDisabled = webservicesDisabled || !isAddToCartEnabled;
  if (isPMA) {
    addToCartDisabled = false;
  }
  if (!userIsCustomer()) {
    addToCartDisabled = false;
  }
  if (webservicesDisabled) {
    addToCartDisabled = true;
  }

  const restrictedQuery = usePackageRestrictionContext();
  const openAddToCartModal = (e?: MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }
    const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
    const context = {
      page: reco_place,
      recommendationPlace,
      recommendationType,
    };
    AddToCartService.openAddToCartWizard(
      {
        productCode: product.code,
        restrictionQuery: restrictedQuery ?? "",
        initialData: formProps.getValues(),
        source: "pdp",
      },
      context,
    );
  };

  const selectClick = () => {
    trackPdpClick("Select Product", productDescriptor.base.designation);
    openAddToCartModal();
  };

  const openModalWishlist = (e?: MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }
    if (userIsPublic()) {
      redirectToUrl(getRedirectToRegisterPage({ openLogin: true }));
    } else {
      trackPdpWishlistClick(productDescriptor.base.designation, 1);

      openAddToWishlistWizard({
        productCode: product.code,
        initialQuantity: formProps.getValues("quantity"),
        initialPriceReference: priceReference,
        addTracking: (product) => trackPdpWishlistClick(product.base.designation, 2),
        selectedOptionalParts: mapOptionalParts4Wishlist(productDescriptor.base, optionalParts),
      });
    }
  };

  const hasSurcharge = (main.netPrice?.surcharge ?? 0) > 0;
  const canSeePrice = userHasPermission(Permission.PRICE);

  const buttons = renderButtons(productDescriptor, {
    atc: {
      disabled: addToCartDisabled || variantLoading,
      onClick: selectClick,
    },
    wishlist: {
      disabled: variantLoading,
      onClick: openModalWishlist,
    },
    configurator: {
      disabled: variantLoading,
      onClick: () => changeTab(TabId.CONFIGURATOR),
    },
    atcFromSP: {
      disabled: variantLoading,
      quantity,
    },
  });

  const mmdCmnData = usePdpCmnLogic();

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <div className={"scha__pdp__product-box"}>
        <Stack gap={2}>
          {displayMMDAssistant && (
            <MMDAssistant
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
                ...mmdCmnData,
              }}
            />
          )}

          <Stack gap={2} direction={{ phone: "column", tablet: "row", "desktop-s": "column" }}>
            <Stack gap={2}>
              <Box sx={{ maxWidth: "328px" }}>
                {/* {hasSales && <SaleTag className={classesExtended.saleTag} />} */}
                <Product.PrimaryImageWithLogosV2 logoWidth={66} gap={24} size={"1fr"} />
              </Box>
              {isKit && <HousingMessages.NotFinal />}
            </Stack>

            <ProductDetailsSection />
          </Stack>

          {isError && t("hybrisAjaxError")}

          {isLoading && <CircleSpinner />}

          {!isLoading && canSeePrice && (
            <PdpPriceBlock
              descriptor={productDescriptor}
              packCode={packCode}
              quantity={quantity}
              priceReference={priceReference}
            />
          )}

          {isOptime && <OptimeSolutionsMessage />}

          {isMobile ? <MobileVariantDrawer /> : <VariantSelect />}

          {isPriceRefVisible && (
            <PriceReferenceParam
              data-test-id={"pdp-price-reference"}
              priceReference={priceReference}
              onChange={(value) => formProps.setValue("priceReference", value ?? "")}
              product={productDescriptor.base}
              editable={qtyInputEnabled}
              externalLabel
              disabled={variantLoading}
            />
          )}

          {quantityVisible && (
            <Stack gap={1}>
              <QuantityInput
                min={1}
                data-test-id={"pdp-input-quantity"}
                max={MAX_QUANTITY}
                value={quantity}
                disabled={!qtyInputEnabled || variantLoading}
                step={1}
                variant={"compact"}
                onValueChange={(newVal) =>
                  formProps.setValue("quantity", newVal, { shouldTouch: true, shouldDirty: true })
                }
              />
              <InfoOrderHandlingUnitQuantity
                preferredPackQuantity={pack?.preferredPackQuantity ?? null}
                packSize={packSize}
                quantity={quantity}
              />
            </Stack>
          )}

          {showBetterDeal && <Alert severity="info">{t("addProduct.scalingDeal")}</Alert>}

          {!isLoading && showCartButton && canSeePrice && productDescriptor.isError && (
            <Alert severity="error">{t("addProduct.wsError")}</Alert>
          )}

          {isPMA && userIsCustomer() && <InfoProductMessagePMA />}

          <Stack gap={1}>{buttons}</Stack>

          {!tariffSurcharge && <IncludesSurchargeDisclaimer hasSurcharge={hasSurcharge} tariffSurcharge={false} />}
        </Stack>
      </div>
    </ProductDescriptorContext>
  );
};
